<!-- (c) TwistedMinds, Gerry Uytdenhouwen - 3 november 2026 -->
<template>
    <div class="orders-table-v2">
        <div class="orders-table-v2__heading">
            <div class="orders-table-v2__kicker">{{ $t('general.orders') }}</div>
            <slot name="heading-right"></slot>
        </div>
        <slot name="toolbar"></slot>
        <div class="orders-table-v2__divider"></div>
        <v-data-table
            :value="selected"
            :loading="loading"
            :page="page"
            :items-per-page="itemsPerPage"
            :server-items-length="serverItemsLength"
            :footer-props="{'items-per-page-options': [5, 10, 15, 100]}"
            :headers="headers"
            :loading-text="$t('general.loading-please-wait')"
            :items="items"
            item-key="id"
            show-select
            hide-default-header
            :item-class="fetchClassItem"
            @input="$emit('update:selected', $event)"
            @update:page="$emit('update:page', $event)"
            @update:items-per-page="$emit('update:itemsPerPage', $event)"
            @pagination="$emit('pagination', $event)"
        >
            <template v-slot:header="{ props, on }">
                <thead>
                <tr>
                    <th v-for="header in props.headers" :key="header.value"
                        :class="['orders-table-v2__head', 'text-' + (header.align || 'start')]">
                        <v-tooltip bottom v-if="header.value === 'data-table-select'">
                            <template v-slot:activator="{ on: tooltipOn, attrs }">
                                <span v-bind="attrs" v-on="tooltipOn">
                                    <v-simple-checkbox
                                        :value="props.everyItem"
                                        :indeterminate="props.someItems && !props.everyItem"
                                        :ripple="false"
                                        @input="on['toggle-select-all']($event)"
                                    />
                                </span>
                            </template>
                            <span>{{ $t('general.tt_select_page') }}</span>
                        </v-tooltip>
                        <v-tooltip bottom v-else :disabled="!header.tooltipKey">
                            <template v-slot:activator="{ on: tooltipOn, attrs }">
                                <a v-if="header.sortable" v-bind="attrs" v-on="tooltipOn"
                                   class="orders-table-v2__sort"
                                   :class="{'orders-table-v2__sort--active': sortKey === header.value}"
                                   @click="$emit('sort', header.value)">
                                    {{ header.text }}
                                    <v-icon x-small class="ml-1">{{ sortIcon(header.value) }}</v-icon>
                                </a>
                                <span v-else v-bind="attrs" v-on="tooltipOn">{{ header.text }}</span>
                            </template>
                            <span>{{ header.tooltipKey ? $t('general.' + header.tooltipKey) : '' }}</span>
                        </v-tooltip>
                    </th>
                </tr>
                </thead>
            </template>

            <template v-slot:item.id="{ item }">
                <span class="orders-table-v2__id">{{ item.id }}</span>
            </template>

            <template v-slot:item.machine_order="{ item }">
                <v-tooltip bottom v-if="item.delivery_type === 'pick_up_vending'">
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">M</span>
                    </template>
                    <span>{{ $t('general.tt_machine_cell') }}</span>
                </v-tooltip>
                <span v-else>-</span>
            </template>

            <template v-slot:item.indicator_order="{ item }">
                <div class="orders-table-v2__indicators">
                    <v-tooltip bottom v-for="indicator in rowIndicators(item)" :key="indicator.type">
                        <template v-slot:activator="{ on, attrs }">
                            <div class="orders-table-v2__indicator" v-bind="attrs" v-on="on">
                                <span class="orders-table-v2__dot" :style="{background: indicator.color}"></span>
                                {{ $t('general.additional-' + indicator.type) }}
                            </div>
                        </template>
                        <span>{{ $t('general.tt_' + indicator.type) }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template v-slot:item.name="{ item }">
                <div class="orders-table-v2__name">{{ item.name }}</div>
                <div class="orders-table-v2__sub" v-if="item.email">{{ item.email }}</div>
                <div class="orders-table-v2__sub" v-if="item.phone">{{ item.phone }}</div>
            </template>

            <template v-slot:item.details="{ item }">
                <div class="orders-table-v2__details"
                     :class="{'orders-table-v2__details--open': isDetailsOpen(item)}">
                    <div class="orders-table-v2__details-text" :data-order-id="item.id">{{ visibleDetailsLines(item).join('\n') }}</div>
                    <v-tooltip bottom v-if="hasMoreDetails(item)">
                        <template v-slot:activator="{ on, attrs }">
                            <a class="orders-table-v2__more" v-bind="attrs" v-on="on"
                               @click.stop="toggleDetails(item)">
                                {{ isDetailsOpen(item) ? $t('general.show_less') : $t('general.show_more') }}
                            </a>
                        </template>
                        <span>{{ isDetailsOpen(item) ? $t('general.tt_show_less') : $t('general.tt_show_more') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template v-slot:item.time="{ item }">
                <div class="orders-table-v2__when">
                    {{ item.date }} <strong v-if="item.time">{{ item.time }}</strong>
                </div>
                <v-tooltip bottom v-if="item.delivery_type === 'delivery'">
                    <template v-slot:activator="{ on, attrs }">
                        <span class="orders-table-v2__pill orders-table-v2__pill--off" v-bind="attrs" v-on="on">
                            {{ $t('general.delivery') }}
                        </span>
                    </template>
                    <span>{{ $t('general.tt_delivery') }}</span>
                </v-tooltip>
            </template>

            <template v-slot:item.status="{ item }">
                <div class="orders-table-v2__pills" v-if="item.status">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span class="orders-table-v2__pill orders-table-v2__pill--status"
                                  :class="statusColor(item) + '--text'" v-bind="attrs" v-on="on">
                                {{ $t('general.' + statusKey(item.status)) }}
                            </span>
                        </template>
                        <span>{{ $t('general.tt_' + statusKey(item.status)) }}</span>
                    </v-tooltip>
                    <v-tooltip bottom v-if="showNewPill(item)">
                        <template v-slot:activator="{ on, attrs }">
                            <span class="orders-table-v2__pill orders-table-v2__pill--status warning--text"
                                  v-bind="attrs" v-on="on">
                                {{ $t('general.new') }}
                            </span>
                        </template>
                        <span>{{ $t('general.tt_new') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template v-slot:item.is_printed="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span class="orders-table-v2__pill" v-bind="attrs" v-on="on"
                              :class="isFlagOn(item.is_printed) ? 'orders-table-v2__pill--on' : 'orders-table-v2__pill--off'">
                            {{ isFlagOn(item.is_printed) ? $t('general.yes') : $t('general.no') }}
                        </span>
                    </template>
                    <span>{{ isFlagOn(item.is_printed) ? $t('general.tt_printed_yes') : $t('general.tt_printed_no') }}</span>
                </v-tooltip>
            </template>

            <template v-slot:item.notified_at="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span class="orders-table-v2__pill" v-bind="attrs" v-on="on"
                              :class="isFlagOn(item.notified_at) ? 'orders-table-v2__pill--on' : 'orders-table-v2__pill--off'">
                            {{ isFlagOn(item.notified_at) ? $t('general.yes') : $t('general.no') }}
                        </span>
                    </template>
                    <span>{{ mailedTooltip(item) }}</span>
                </v-tooltip>
            </template>

            <template v-slot:item.amount="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span class="orders-table-v2__total" v-bind="attrs" v-on="on">{{ item.amount }}</span>
                    </template>
                    <span>{{ $t('general.tt_total') }}</span>
                </v-tooltip>
            </template>

            <template v-slot:item.actions="{ item }">
                <div class="orders-table-v2__actions">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <button type="button" class="orders-table-v2__action" @click="$emit('show', item)">
                                    <v-icon small>mdi-eye</v-icon>
                                </button>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_show') }}</span>
                    </v-tooltip>
                    <!-- without the vending button an empty cell keeps edit, mail and delete on the second row -->
                    <v-tooltip bottom v-if="vendingEnabled">
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <button type="button" class="orders-table-v2__action"
                                        :disabled="loadingVending || Boolean(item['pickup_delivery_vending'])"
                                        @click="$emit('vending', item)">
                                    <v-icon small>mdi-storefront</v-icon>
                                </button>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_vending') }}</span>
                    </v-tooltip>
                    <span v-else></span>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <button type="button" class="orders-table-v2__action"
                                        :disabled="Boolean(item.pdf_loading)"
                                        @click="$emit('pdf', item)">
                                    <v-icon small>mdi-file-pdf-box</v-icon>
                                </button>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_pdf') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <button type="button" class="orders-table-v2__action" @click="$emit('edit', item)">
                                    <v-icon small>mdi-pencil</v-icon>
                                </button>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_edit') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <button type="button" class="orders-table-v2__action" @click="$emit('email', item)">
                                    <v-icon small>mdi-email</v-icon>
                                </button>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_email') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <button type="button" class="orders-table-v2__action orders-table-v2__action--delete"
                                        @click="$emit('delete', item)">
                                    <v-icon small>mdi-delete</v-icon>
                                </button>
                            </span>
                        </template>
                        <span>{{ $t('general.tt_delete') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template v-slot:no-data></template>
        </v-data-table>
    </div>
</template>

<script>
import {checkIndicatorOrder, fetchClassItem} from "@/helpers/dashboard/orders";
import {
    DETAILS_MAX_LINES,
    STATUS_COLOR,
    detailsLines,
    formatNotifiedAt,
    isFlagOn,
    isNewOrder,
    statusKey,
} from "@/utils/ordersTableV2";

// Indicator dots, in the order they are listed in a cell.
const INDICATORS = [
    {type: 'bread', color: '#ffc107'},
    {type: 'vegetables', color: '#4caf50'},
    {type: 'meat', color: '#ba1e1e'},
];

export default {
    name: "OrdersTableV2",
    props: {
        items: {type: Array, default: () => []},
        selected: {type: Array, default: () => []},
        page: {type: Number, default: 1},
        itemsPerPage: {type: Number, default: 100},
        serverItemsLength: {type: Number, default: 0},
        loading: {type: Boolean, default: false},
        // optional columns, same conditions as filteredHeaders in Index.vue
        columns: {
            type: Object,
            default: () => ({machine: false, indicator: false, details: false}),
        },
        sortKey: {type: String, default: 'id'},
        orderByAsc: {type: Boolean, default: false},
        vendingEnabled: {type: Boolean, default: false},
        loadingVending: {type: Boolean, default: false},
    },
    data: () => ({
        detailsMaxLines: DETAILS_MAX_LINES,
        // order id -> true while its full details are shown
        openDetails: {},
        // order id -> true when its details are cut off visually (long lines that wrap)
        clampedDetails: {},
    }),
    watch: {
        items() {
            this.measureClampedDetails();
        },
        'columns.details'() {
            this.measureClampedDetails();
        },
    },
    mounted() {
        this.measureClampedDetails();
    },
    computed: {
        headers() {
            const allHeaders = [
                {labelKey: 'id', value: 'id', sortable: true, tooltipKey: 'tt_id'},
                {labelKey: 'machine-order', value: 'machine_order', sortable: true, tooltipKey: 'tt_machine', column: 'machine'},
                {labelKey: 'indicator-order', value: 'indicator_order', tooltipKey: 'tt_indicator', column: 'indicator'},
                {labelKey: 'name', value: 'name', tooltipKey: 'tt_name'},
                {labelKey: 'details', value: 'details', tooltipKey: 'tt_details', column: 'details'},
                {labelKey: 'pickup_time', value: 'time', sortable: true, tooltipKey: 'tt_pickup_time'},
                {labelKey: 'status', value: 'status', tooltipKey: 'tt_status'},
                {labelKey: 'printed', value: 'is_printed', tooltipKey: 'tt_printed'},
                {labelKey: 'mailed', value: 'notified_at', tooltipKey: 'tt_mailed'},
                {labelKey: 'total', value: 'amount', align: 'end', tooltipKey: 'tt_total'},
                {labelKey: 'actions', value: 'actions', align: 'end', tooltipKey: 'tt_actions'},
            ];
            return allHeaders
                .filter(header => !header.column || this.columns[header.column])
                .map(header => ({...header, text: this.$t('general.' + header.labelKey)}));
        },
    },
    methods: {
        fetchClassItem,
        statusKey,
        isFlagOn,
        sortIcon(headerValue) {
            if (this.sortKey !== headerValue) {
                return '';
            }
            return this.orderByAsc ? 'mdi-arrow-down' : 'mdi-arrow-up';
        },
        rowIndicators(item) {
            return INDICATORS.filter(indicator => checkIndicatorOrder(item, indicator.type));
        },
        // detail lines without trailing blank lines
        visibleDetailsLines(item) {
            const lines = detailsLines(item.details);
            while (lines.length && lines[lines.length - 1].trim() === '') {
                lines.pop();
            }
            return lines;
        },
        hasMoreDetails(item) {
            return this.visibleDetailsLines(item).length > this.detailsMaxLines
                || Boolean(this.clampedDetails[item.id]);
        },
        // checks once per data change (not per render) which closed details cells overflow their 4-line clamp
        measureClampedDetails() {
            this.$nextTick(() => {
                if (!this.$el || !this.$el.querySelectorAll) {
                    return;
                }
                const clamped = {};
                this.$el.querySelectorAll('.orders-table-v2__details-text[data-order-id]').forEach(element => {
                    const orderId = element.getAttribute('data-order-id');
                    clamped[orderId] = this.openDetails[orderId]
                        ? Boolean(this.clampedDetails[orderId])
                        : element.scrollHeight > element.clientHeight + 1;
                });
                this.clampedDetails = clamped;
            });
        },
        isDetailsOpen(item) {
            return Boolean(this.openDetails[item.id]);
        },
        toggleDetails(item) {
            this.$set(this.openDetails, item.id, !this.isDetailsOpen(item));
        },
        statusColor(item) {
            return STATUS_COLOR[statusKey(item.status)];
        },
        // same rule as the existing table: a canceled order never gets the "new" label
        showNewPill(item) {
            return isNewOrder(item) && statusKey(item.status) !== 'canceled';
        },
        mailedTooltip(item) {
            if (!isFlagOn(item.notified_at)) {
                return this.$t('general.tt_mailed_no');
            }
            return this.$t('general.tt_mailed_yes', {date: formatNotifiedAt(item.notified_at)});
        },
    },
};
</script>

<style lang="scss">
.orders-table-v2 {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, .06);
    padding: 18px 20px;
    color: #0d3b47;

    .orders-table-v2__heading {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 8px 12px;
        margin-bottom: 8px;
    }

    .orders-table-v2__divider {
        height: 1px;
        margin: 12px -20px;
        background: #e8ecee;
    }

    .orders-table-v2__kicker {
        color: #e0573a;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .12em;
        text-transform: uppercase;
    }

    .theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th.orders-table-v2__head {
        height: auto;
        padding: 6px 6px 8px;
        font-size: 11px;
        font-weight: 600;
        color: #6b7a80;
        text-transform: uppercase;
        letter-spacing: .05em;
        white-space: nowrap;
        vertical-align: bottom;
        border-bottom: 1px solid #e8ecee;
    }

    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
        height: auto;
        padding: 12px 6px;
        font-size: 13px;
        vertical-align: top;
    }

    .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row) {
        border-bottom: 1px solid #e8ecee;
    }

    .v-data-table__checkbox,
    .v-simple-checkbox {
        margin-top: -2px;
    }

    .orders-table-v2__sort {
        color: #6b7a80 !important;
        display: inline-flex;
        align-items: center;
    }

    .orders-table-v2__sort--active {
        color: #1d6f86 !important;
    }

    .orders-table-v2__id {
        color: #6b7a80;
        font-size: 12px;
        font-variant-numeric: tabular-nums;
    }

    .orders-table-v2__indicators {
        display: flex;
        flex-direction: column;
        font-size: 12px;
        line-height: 1.6;
        white-space: nowrap;
    }

    .orders-table-v2__dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 5px;
    }

    .orders-table-v2__name {
        font-size: 14px;
        font-weight: 700;
    }

    .orders-table-v2__sub {
        color: #6b7a80;
        font-size: 12px;
        line-height: 1.45;
    }

    .orders-table-v2__details {
        width: 160px;
        font-size: 12px;
        line-height: 1.5;
    }

    .orders-table-v2__details-text {
        white-space: pre-line;
        overflow-wrap: anywhere;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .orders-table-v2__details--open .orders-table-v2__details-text {
        display: block;
        -webkit-line-clamp: unset;
    }

    .orders-table-v2__more {
        display: inline-block;
        margin-top: 2px;
        color: #1d6f86;
        font-size: 12px;
        font-weight: 600;
    }

    .orders-table-v2__when {
        white-space: nowrap;

        strong {
            color: #1d6f86;
        }
    }

    .orders-table-v2__pills {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 4px;
    }

    .orders-table-v2__pill {
        position: relative;
        display: inline-block;
        border-radius: 999px;
        padding: 2px 9px;
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
    }

    // soft label in the current theme colour: the text colour comes from the theme class
    .orders-table-v2__pill--status::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: currentColor;
        opacity: .12;
    }

    .orders-table-v2__pill--on {
        background: #e2f3ec;
        color: #1f7a55;
    }

    .orders-table-v2__pill--off {
        background: #f1f3f4;
        color: #8f9ba0;
    }

    .orders-table-v2__total {
        font-weight: 700;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }

    .orders-table-v2__actions {
        display: inline-grid;
        grid-template-columns: repeat(3, 26px);
        gap: 4px;
        margin-left: 12px;
    }

    .orders-table-v2__action {
        width: 26px;
        height: 26px;
        border-radius: 7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #f2f4f5;

        .v-icon {
            color: #5d6b70;
        }

        &:disabled {
            opacity: .4;
            cursor: default;
        }
    }

    .orders-table-v2__action--delete .v-icon {
        color: #c0392b;
    }
}
</style>
